import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from './firebase'; 
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import './ResidentDashboard.css';
import './AdminDashboard.css';

// Turns "2026-05" into "May 2026" for a friendlier bill label. Falls back to
// the raw string for anything unexpected so a bad/missing value never blanks
// out the row - this only touches display, never the underlying bill data.
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function formatMonthLabel(monthStr) {
  if (!monthStr || typeof monthStr !== 'string') return monthStr || '';
  const parts = monthStr.split('-');
  if (parts.length !== 2) return monthStr;
  const [y, m] = parts;
  const idx = parseInt(m, 10) - 1;
  if (Number.isNaN(idx) || idx < 0 || idx > 11) return monthStr;
  return `${MONTH_NAMES[idx]} ${y}`;
}

function ResidentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userRefInfo, setUserRefInfo] = useState({ propertyType: "", plotNo: "" });
  const [profileData, setProfileData] = useState(null);
  const [ratePlan, setRatePlan] = useState(null); 
  const [checkoutData, setCheckoutData] = useState(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false); // <-- NEW STATE
  const [paymentRestrictionMsg, setPaymentRestrictionMsg] = useState(null); // <-- NEW STATE FOR RESTRICTION POPUP
  const [showReturnToAppPrompt, setShowReturnToAppPrompt] = useState(false); // Shown after payment when the resident arrived here from the mobile app
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [promptPasswordForm, setPromptPasswordForm] = useState({ newPassword: "", confirmPassword: "" });
  const [promptSaving, setPromptSaving] = useState(false);
  const API_BASE_URL = "https://api.bathindahousingcomplex.com";
  
  const [currentView, setCurrentView] = useState('dashboard');
  const [saving, setSaving] = useState(false);

  const [bills, setBills] = useState([]);
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [advanceMonths, setAdvanceMonths] = useState(0);
  
  // --- NEW STATES FOR YEARLY ---
  const [selectedYearlyBillIds, setSelectedYearlyBillIds] = useState([]);
  const [advanceYears, setAdvanceYears] = useState(0);

  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState({});

  // Pagination States
  const [noticesPage, setNoticesPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const itemsPerPage = 5;
  const [expandedHistory, setExpandedHistory] = useState({});

  const [editForm, setEditForm] = useState({
    ownerName: "", phone: "", address: "", uidNo: "", newPassword: "", confirmPassword: ""
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (!user.uid.includes('_')) {
          alert("Invalid Resident Session. Please login again.");
          signOut(auth);
          return;
        }

        // Look for the LAST underscore to safely split 'c_buildings_10' into 'c_buildings' and '10'
        const lastUnderscoreIndex = user.uid.lastIndexOf('_');
        
        const propType = user.uid.substring(0, lastUnderscoreIndex);
        const pNo = user.uid.substring(lastUnderscoreIndex + 1);
        
        setUserRefInfo({ propertyType: propType, plotNo: pNo });
        
        await fetchProfileData(propType, pNo);
        await fetchRatePlan(propType);
        await fetchBills(propType, pNo);
        await fetchContacts();
        await fetchMessages(); 
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchProfileData = async (type, plot) => {
    try {
      const docRef = doc(db, type, plot);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setProfileData(data);
        setEditForm({
          ownerName: data.ownerName || data.name || "",
          phone: data.phone || data.mobile || "",
          address: data.address || "",
          uidNo: data.uidNo || data.uid || "",
          newPassword: "",
          confirmPassword: ""
        });

        // Trigger the password change prompt if using default password
        if (data.password === plot) {
            setShowPasswordPrompt(true);
        }
      }
    } catch (error) { console.error("Error fetching data:", error); }
    setLoading(false);
  };

  const fetchRatePlan = async (type) => {
    try {
      const docRef = doc(db, "rates_plan", type);
      const snap = await getDoc(docRef);
      if (snap.exists()) setRatePlan(snap.data());
    } catch (error) { console.error("Error fetching rates:", error); }
  };

  const fetchBills = async (type, plot) => {
    try {
      const q = query(
        collection(db, "bills"), 
        where("houseNo", "==", plot), 
        where("propertyType", "==", type)
      );
      const snap = await getDocs(q);
      const fetchedBills = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      fetchedBills.sort((a, b) => b.month.localeCompare(a.month)); 
      setBills(fetchedBills);
      
      // Separate initialization for Monthly and Yearly
      const unpaidMonthlyIds = fetchedBills.filter(b => b.status === 'UNPAID' && b.billType !== 'YEARLY').map(b => b.id);
      const unpaidYearlyIds = fetchedBills.filter(b => b.status === 'UNPAID' && b.billType === 'YEARLY').map(b => b.id);
      
      setSelectedBillIds(unpaidMonthlyIds); // Keeps existing monthly logic intact
      setSelectedYearlyBillIds(unpaidYearlyIds);
    } catch (error) { console.error("Error fetching bills:", error); }
  };

  const fetchContacts = async () => {
    try {
      const snap = await getDocs(collection(db, "verified_contacts"));
      const fetchedData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      const grouped = fetchedData.reduce((acc, curr) => {
        const r = curr.role || 'Other Services';
        if (!acc[r]) acc[r] = [];
        acc[r].push(curr);
        return acc;
      }, {});
      setContacts(grouped);
    } catch (error) { console.error("Error fetching contacts:", error); }
  };

  const fetchMessages = async () => {
    try {
      const q = query(collection(db, "resident_messages"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) { console.error("Error fetching messages:", error); }
  };

  const handleLogout = () => { 
    signOut(auth).then(() => {
        navigate('/'); // Redirects to the Home page
    }); 
  };

  const handlePromptPasswordSave = async (e) => {
    e.preventDefault();
    if (promptPasswordForm.newPassword !== promptPasswordForm.confirmPassword) return alert("❌ Passwords do not match!");
    if (promptPasswordForm.newPassword.length < 6) return alert("❌ Password must be at least 6 characters long.");
    
    setPromptSaving(true);
    try {
      const docRef = doc(db, userRefInfo.propertyType, userRefInfo.plotNo);
      await updateDoc(docRef, {
        password: promptPasswordForm.newPassword,
        updatedAt: serverTimestamp()
      });
      alert("✅ Password updated successfully!");
      setShowPasswordPrompt(false);
      setProfileData(prev => ({...prev, password: promptPasswordForm.newPassword}));
    } catch (error) {
      alert("Error updating password: " + error.message);
    }
    setPromptSaving(false);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (editForm.newPassword || editForm.confirmPassword) {
      if (editForm.newPassword !== editForm.confirmPassword) return alert("❌ Passwords do not match!");
      if (editForm.newPassword.length < 6) return alert("❌ Password must be at least 6 characters long.");
    }
    setSaving(true);
    try {
      const docRef = doc(db, userRefInfo.propertyType, userRefInfo.plotNo);
      const updates = {
        ownerName: editForm.ownerName,
        phone: editForm.phone,
        address: editForm.address,
        uidNo: editForm.uidNo,
        updatedAt: serverTimestamp()
      };
      if (editForm.newPassword) updates.password = editForm.newPassword;

      await updateDoc(docRef, updates);
      alert("✅ Profile details updated successfully!");
      await fetchProfileData(userRefInfo.propertyType, userRefInfo.plotNo);
      setCurrentView('dashboard');
    } catch (error) { alert("Error updating profile: " + error.message); }
    setSaving(false);
  };

  const prepareCheckout = async (waivePenalty) => {
    if (selectedBillIds.length === 0 && advanceMonths === 0) return alert("Select bills or advance months.");
    setIsProcessingCheckout(true);

    // Enforce the maximum 12 months rule
    const advMonths = waivePenalty ? Math.min(selectedPenaltyCount, 12) : advanceMonths;

    try {
        const idToken = await auth.currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/create-payment-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({
                houseNo: userRefInfo.plotNo,
                propertyType: userRefInfo.propertyType,
                billIds: selectedBillIds,
                advanceMonths: advMonths,
                waivePenaltyOffer: waivePenalty
            })
        });

        const data = await response.json();
        if (!data.success) throw new Error(data.error);

        setCheckoutData({
                ...data,
                waivePenalty: waivePenalty,
                advanceMonthsApplied: advMonths
            });
        } catch (err) {
            // Instead of a browser alert, we trigger the custom red popup modal
            setPaymentRestrictionMsg(err.message);
        }
        setIsProcessingCheckout(false);
  };

  const launchRazorpay = async () => {
    if (!checkoutData) return;

    const options = {
        key: "rzp_live_Sms01Pi653IXpu", // IMPORTANT: Add your Test Key ID here
        amount: checkoutData.amountInPaise,
        currency: "INR",
        name: "Bathinda Housing Complex",
        description: "Maintenance Bill Payment",
        order_id: checkoutData.razorpayOrderId,
        handler: async function (response) {
            try {
                setIsVerifying(true); // <-- LOCK THE UI
                const idToken = await auth.currentUser.getIdToken();
                const verifyRes = await fetch(`${API_BASE_URL}/verify-payment`, {
                     method: 'POST',
                     headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
                     body: JSON.stringify({
                         razorpay_order_id: response.razorpay_order_id,
                         razorpay_payment_id: response.razorpay_payment_id,
                         razorpay_signature: response.razorpay_signature
                     })
                });
                const verifyData = await verifyRes.json();
                if (verifyData.success) {
                     setCheckoutData(null);
                     setSelectedBillIds([]);
                     setSelectedYearlyBillIds([]); // Clear yearly
                     setAdvanceMonths(0);
                     setAdvanceYears(0); // Clear yearly
                     await fetchBills(userRefInfo.propertyType, userRefInfo.plotNo);
                     // If this session started from the mobile app's "Pay Your Bills"
                     // hand-off, offer to send them straight back instead of a plain alert.
                     if (sessionStorage.getItem('housefed_from_app') === 'true') {
                       setShowReturnToAppPrompt(true);
                     } else {
                       alert("✅ Payment Successful! Your ledger has been updated.");
                     }
                } else {
                     alert("Payment verification failed.");
                }
            } catch(err) {
                alert("Verification error: " + err.message);
            } finally {
                setIsVerifying(false); // <-- UNLOCK UI (if it didn't close automatically)
            }
        },
        prefill: {
            name: profileData.ownerName || "",
            contact: profileData.phone || ""
        },
        theme: { color: "#2c3e50" }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response){
        alert("Payment Failed: " + response.error.description);
    });
    rzp.open();
  };

  const handleYearlyToggle = (id) => {
    setSelectedYearlyBillIds(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };

  const prepareYearlyCheckout = async () => {
    if (selectedYearlyBillIds.length === 0 && advanceYears === 0) return alert("Select yearly bills or advance years.");
    setIsProcessingCheckout(true);

    try {
        const idToken = await auth.currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/create-payment-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({
                houseNo: userRefInfo.plotNo,
                propertyType: userRefInfo.propertyType,
                billIds: selectedYearlyBillIds,
                advanceMonths: 0, // No monthly advance
                advanceYears: advanceYears,
                waivePenaltyOffer: false, // No waiver for yearly
                paymentCategory: 'YEARLY' // Flag for backend separation
            })
        });

        const data = await response.json();
        if (!data.success) throw new Error(data.error);

        setCheckoutData({
            ...data,
            waivePenalty: false,
            advanceMonthsApplied: 0
        });
    } catch (err) {
        setPaymentRestrictionMsg(err.message);
    }
    setIsProcessingCheckout(false);
  };

  const handleBillToggle = (id) => {
    setSelectedBillIds(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };

  if (loading || !profileData) return <div style={{padding:'50px', textAlign:'center'}}>Loading Secure Resident Portal...</div>;

  const formatPropertyType = (type) => {
    const map = { 'houses': 'House', 'shops': 'Shop', 'booths': 'Booth', 'c_buildings': 'Commercial Building', 'n_home': 'Nursing Home' };
    return map[type] || type;
  };

  // --- DERIVED DATA ---
  const paidBills = bills.filter(b => b.status === 'PAID' || b.status === 'PAID_IN_ADVANCE');
  
  // Split the unpaid bills
  const monthlyUnpaidBills = bills.filter(b => b.status === 'UNPAID' && b.billType !== 'YEARLY');
  const yearlyUnpaidBills = bills.filter(b => b.status === 'UNPAID' && b.billType === 'YEARLY');
  
  const isMonthlyDefaulter = monthlyUnpaidBills.length > 0;
  const isYearlyDefaulter = yearlyUnpaidBills.length > 0;
  const isDefaulter = isMonthlyDefaulter || isYearlyDefaulter;

  // Monthly Calculations (Untouched, just using the filtered list)
  const selectedBillsData = monthlyUnpaidBills.filter(b => selectedBillIds.includes(b.id));
  const selectedPenaltyCount = selectedBillsData.filter(b => (b.penalty?.amount || 0) > 0).length;
  const selectedTotal = selectedBillsData.reduce((sum, b) => sum + (b.totalPayable || 0) + (b.penalty?.amount || 0), 0);

  // Yearly Calculations
  const selectedYearlyData = yearlyUnpaidBills.filter(b => selectedYearlyBillIds.includes(b.id));
  const selectedYearlyTotal = selectedYearlyData.reduce((sum, b) => sum + (b.totalPayable || 0) + (b.penalty?.amount || 0), 0);

  const relevantMessages = messages.filter(msg => {
    const t = msg.target;
    if (!t) return true;
    if (t.type === 'ALL') return true;
    if (t.type === 'DEFAULTERS' && isDefaulter) return true;
    if (t.type === 'PROPERTY' && t.propertyType === userRefInfo.propertyType) return true;
    if (t.type === 'SPECIFIC' && t.propertyType === userRefInfo.propertyType && t.plotNo === userRefInfo.plotNo) return true;
    return false;
  });

  // --- PAGINATION SLICES ---
  const totalNoticesPages = Math.ceil(relevantMessages.length / itemsPerPage) || 1;
  const currentNotices = relevantMessages.slice((noticesPage - 1) * itemsPerPage, noticesPage * itemsPerPage);

  const totalHistoryPages = Math.ceil(paidBills.length / itemsPerPage) || 1;
  // History page slice is handled after aggregation below

  return (
    <div className="resident-wrapper">
      
      <header className="admin-header" style={{marginBottom: 0, position: 'relative', zIndex: 20}}>
        <div className="container header-flex">
          <h2>Resident Portal</h2>
          <div className="header-actions">
            <button onClick={() => navigate('/')} className="btn-icon">🏠 Home</button>
            <button onClick={() => setCurrentView(currentView === 'dashboard' ? 'settings' : 'dashboard')} className="btn-icon">
              {currentView === 'dashboard' ? '⚙️ Settings' : '← Back to Dashboard'}
            </button>
            <button onClick={handleLogout} className="btn-logout">Logout</button>
          </div>
        </div>
      </header>

      <div className="resident-banner fade-in-up">
        <div className="container">
          <h1>Welcome, {profileData.ownerName || profileData.name || "Resident"}</h1>
          <p>{formatPropertyType(userRefInfo.propertyType)} #{profileData['Plot no'] || profileData.id}</p>
        </div>
      </div>

      <div className="container" style={{marginTop: '40px'}}>
        
        {currentView === 'dashboard' && (
           <div className="fade-in-up">
             
             {/* --- 1. TOP ROW: NOTICES & SERVICES DIRECTORY (Side by Side) --- */}
             <div className="info-grid" style={{marginBottom: '30px'}}>
               
               {/* 1A. SOCIETY NOTICES */}
               <div className="info-card" style={{display: 'flex', flexDirection: 'column'}}>
                 <div className="info-card-header" style={{borderBottom: 'none', paddingBottom: 0}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '10px 0' }}>
                        <div className="icon-box-primary">📢</div>
                        <h3 style={{color: 'var(--dark-text)', margin: 0}}>Society Notices</h3>
                    </div>
                 </div>
                 
                 {relevantMessages.length === 0 ? (
                    <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'}}>
                        <p style={{color: '#888', fontStyle: 'italic'}}>No new notices.</p>
                    </div>
                 ) : (
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                        <div className="message-board" style={{flex: 1, maxHeight: '350px', overflowY: 'auto', paddingRight: '5px', marginTop: '15px'}}>
                        {currentNotices.map(msg => (
                            <div key={msg.id} className={`message-alert ${msg.target?.type === 'DEFAULTERS' ? 'defaulter' : ''}`}>
                            <div className="message-header">
                                <h4 className="message-title">
                                {msg.target?.type === 'DEFAULTERS' ? '⚠️ ' : ''}{msg.title}
                                </h4>
                                <span className="message-date">{msg.createdAt ? new Date(msg.createdAt.seconds * 1000).toLocaleDateString() : 'New'}</span>
                            </div>
                            <div className="message-body">{msg.body}</div>
                            {msg.fileUrl && (
                                <a href={msg.fileUrl} target="_blank" rel="noreferrer" style={{fontSize: '13px', color: '#3498db', fontWeight: 'bold', display: 'inline-block', marginTop: '5px'}}>
                                📎 View Attached Document
                                </a>
                            )}
                            </div>
                        ))}
                        </div>
                        
                        {/* Notices Pagination */}
                        {relevantMessages.length > itemsPerPage && (
                            <div className="pagination-controls">
                                <button className="btn-page" disabled={noticesPage === 1} onClick={() => setNoticesPage(p => p - 1)}>← Prev</button>
                                <span className="page-info">Page {noticesPage} of {totalNoticesPages}</span>
                                <button className="btn-page" disabled={noticesPage === totalNoticesPages} onClick={() => setNoticesPage(p => p + 1)}>Next →</button>
                            </div>
                        )}
                    </div>
                 )}
               </div>

               {/* 1B. ESSENTIAL SERVICES DIRECTORY */}
               <div className="info-card" style={{display: 'flex', flexDirection: 'column'}}>
                 <div className="info-card-header" style={{borderBottom: 'none', paddingBottom: 0}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '10px 0' }}>
                        <div className="icon-box-glass">👷‍♂️</div>
                        <h3 style={{color: 'var(--dark-text)', margin: 0}}>Essential Services</h3>
                    </div>
                 </div>

                 {Object.keys(contacts).length === 0 ? (
                    <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'}}>
                        <p style={{color: '#888', fontStyle: 'italic'}}>No contacts added yet.</p>
                    </div>
                 ) : (
                    <div style={{flex: 1, maxHeight: '350px', overflowY: 'auto', paddingRight: '10px', marginTop: '15px'}}>
                        <div className="directory-grid">
                        {Object.keys(contacts).sort().map(role => (
                            <div key={role} className="service-group">
                            <h4>{role}</h4>
                            {contacts[role].map(contact => (
                                <div key={contact.id} className="service-card">
                                <h5>{contact.name}</h5>
                                <p>📞 <strong>{contact.phone}</strong></p>
                                {contact.address && <p style={{fontSize: '11px', marginTop: '4px'}}>🏠 {contact.address}</p>}
                                </div>
                            ))}
                            </div>
                        ))}
                        </div>
                    </div>
                 )}
               </div>

             </div>


             {/* --- 2. FULL OWNER INFO --- */}
             <div className="info-grid" style={{marginBottom: '40px'}}>
               <div className="info-card">
                 <div className="info-card-header">
                   <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '10px 0' }}>
                       <div className="icon-box-glass">👤</div>
                       <h3 style={{ margin: 0 }}>Owner Information</h3>
                   </div>
                   <button onClick={() => setCurrentView('settings')} className="btn-edit">Edit ✎</button>
                 </div>
                 <div className="detail-row"><span className="detail-label">Full Name</span><span className="detail-value">{profileData.ownerName || profileData.name || "Not Provided"}</span></div>
                 <div className="detail-row"><span className="detail-label">Contact Number</span><span className="detail-value">{profileData.phone || profileData.mobile || "Not Provided"}</span></div>
                 <div className="detail-row"><span className="detail-label">UID / Aadhaar</span><span className="detail-value">{profileData.uidNo || profileData.uid || "Not Provided"}</span></div>
                 <div className="detail-row"><span className="detail-label">Registered Address</span><span className="detail-value">{profileData.address || "Not Provided"}</span></div>
               </div>

               <div className="info-card">
                 <div className="info-card-header">
                   <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '10px 0' }}>
                       <div className="icon-box-primary">🏠</div>
                       <h3 style={{ margin: 0 }}>Property Specifications</h3>
                   </div>
                   <span className="status-badge badge-blue">System Verified</span>
                 </div>
                 <div className="detail-row"><span className="detail-label">Property Type</span><span className="detail-value">{formatPropertyType(userRefInfo.propertyType)}</span></div>
                 <div className="detail-row"><span className="detail-label">Plot Number</span><span className="detail-value">#{profileData['Plot no'] || profileData.id}</span></div>
                 <div className="detail-row"><span className="detail-label">Total Size</span><span className="detail-value">{profileData.plotSize || profileData.size_yards || "N/A"} Sq. Yards</span></div>
                 <div className="detail-row">
                   <span className="detail-label">Construction Status</span>
                   <span className="detail-value">{profileData.isFilled ? <span className="status-badge badge-green">Constructed</span> : <span className="status-badge badge-red">Vacant Plot</span>}</span>
                 </div>
                 <div className="detail-row">
                   <span className="detail-label">Account Status</span>
                   <span className="detail-value">{profileData.status === 'INACTIVE' ? <span className="status-badge badge-red">INACTIVE</span> : <span className="status-badge badge-green">ACTIVE</span>}</span>
                 </div>
               </div>
             </div>

             <h2 style={{marginBottom: '20px', color: 'var(--dark-text)'}}>🧾 Billing & Payments</h2>
             
             <div className="info-grid">
               
               {/* --- 3A. PENDING DUES --- */}
               <div className="info-card" style={{borderTop: '5px solid #ff6b6b', display: 'flex', flexDirection: 'column'}}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '10px', marginTop: '5px' }}>
                    <div className="icon-box-warning">⚠️</div>
                    <h3 style={{ margin: 0 }}>Pending Dues</h3>
                 </div>
                 <p style={{fontSize: '13px', color: '#666', margin: '0 0 15px 0'}}>Select the generated bills you wish to clear. Note: Maximum 12 penalties can be waived.</p>
                 
                 <div className="bill-scroll-area">
                   {monthlyUnpaidBills.length === 0 ? (
                     <p style={{color: '#2e7d32', fontWeight: 'bold', textAlign: 'center', marginTop: '30px'}}>🎉 You have no pending arrears!</p>
                   ) : (
                     <div className="bill-list-container">
                       {monthlyUnpaidBills.map(bill => {
                         const penaltyAmt = bill.penalty?.amount || 0;
                         const totalAmt = bill.totalPayable + penaltyAmt;
                         const isSelected = selectedBillIds.includes(bill.id);
                         return (
                           <div key={bill.id} className={`bill-row${isSelected ? ' bill-row-selected' : ''}`}>
                             <label className="bill-row-label">
                               <input type="checkbox" className="bill-checkbox" checked={isSelected} onChange={() => handleBillToggle(bill.id)} />
                               <div>
                                 <strong className="bill-row-month" style={{color: bill.billType === 'YEARLY' ? '#c0392b' : '#333'}}>
                                     {bill.billType === 'YEARLY' ? `🚧 Yearly Maintenance (${bill.year})` : formatMonthLabel(bill.month)}
                                 </strong>
                                 <div className="bill-row-due">Due: {new Date(bill.dueDate?.seconds * 1000).toLocaleDateString()}</div>
                                 {bill.breakup && (
                                   <div className="bill-breakup">
                                     {bill.breakup.s_w > 0 && <span>💧 Water/Sewage: ₹{bill.breakup.s_w}</span>}
                                     {bill.breakup.service > 0 && <span>🧹 Service Charge: ₹{bill.breakup.service}</span>}
                                     {bill.breakup.maintenance > 0 && <span style={{color: '#c0392b', fontWeight: 'bold'}}>🏗️ Annual Maintenance: ₹{bill.breakup.maintenance}</span>}
                                   </div>
                                 )}
                               </div>
                             </label>

                             <div className="bill-row-totals">
                               {penaltyAmt > 0 && (
                                 <>
                                   <div className="bill-total-line">
                                     <span>Base</span><span>₹{bill.totalPayable}</span>
                                   </div>
                                   <div className="bill-total-line bill-total-penalty">
                                     <span>Penalty</span><span>+₹{penaltyAmt}</span>
                                   </div>
                                 </>
                               )}
                               <div className="bill-total-line bill-total-final">
                                 <span>Total</span><span>₹{totalAmt}</span>
                               </div>
                             </div>

                             {penaltyAmt > 0 && bill.billType !== 'YEARLY' && (
                               <div className="bill-waiver-chip">💡 Pay 1 advance month to waive this ₹{penaltyAmt} penalty</div>
                             )}
                           </div>
                         );
                       })}
                     </div>
                   )}
                 </div>

                 <button 
                    onClick={() => prepareCheckout(false)} 
                    disabled={selectedBillIds.length === 0 || isProcessingCheckout} 
                    className="btn-primary-full" 
                    style={{background: selectedBillIds.length === 0 ? '#ccc' : '#c0392b'}}
                  >
                    {isProcessingCheckout ? "Preparing..." : (selectedPenaltyCount > 0 ? `Pay Selected Dues with Penalty (₹ ${selectedTotal})` : `Pay Selected Dues (₹ ${selectedTotal})`)}
                  </button>

                  {/* The New Dynamic Green Offer Button */}
                  {selectedPenaltyCount > 0 && (
                    <button
                      onClick={() => prepareCheckout(true)}
                      disabled={isProcessingCheckout}
                      className="btn-primary-full"
                      style={{background: isProcessingCheckout ? '#ccc' : '#27ae60', marginTop: '10px'}}
                    >
                      {selectedPenaltyCount > 12 
                        ? `Pay Dues without Penalty + 12 Advance Months (maxed out)` 
                        : `Pay Dues without Penalty + ${selectedPenaltyCount} Advance Month(s)`}
                    </button>
                  )}
               </div>
               
               {/* --- 3B. ADVANCE PAYMENTS (WITH DEFAULTER LOCK) --- */}
               <div className={`info-card ${isMonthlyDefaulter ? 'locked-section' : ''}`} style={{borderTop: '5px solid #3498db', display: 'flex', flexDirection: 'column'}}>
                 
                 {isMonthlyDefaulter && (
                   <div className="locked-overlay">
                     <span>🔒 Clear Monthly Arrears First</span>
                     <p style={{fontSize: '12px', color: '#333', marginTop: '10px', fontWeight: 'bold', padding: '0 15px'}}>Monthly advance payments are locked. Please pay your pending monthly bills to unlock this feature.</p>
                   </div>
                 )}

                 <h3>🚀 Pay in Advance</h3>
                 <p style={{fontSize: '13px', color: '#666', margin: '0 0 15px 0'}}>Secure future months starting from your last generated bill.</p>
                 
                 <div style={{flex: 1, display: 'flex', flexDirection: 'column', background: '#f8f9fa', borderRadius: '8px', padding: '20px', marginBottom: '15px', border: '1px solid #e0e0e0'}}>
                    <label style={{fontWeight: 'bold', color: '#333', marginBottom: '10px'}}>Select Duration:</label>
                    <select className="input-field" style={{marginBottom: '0', padding: '10px', fontSize: '15px'}} value={advanceMonths} onChange={e => setAdvanceMonths(Number(e.target.value))}>
                       <option value={0}>-- Choose Months --</option>
                       <option value={3}>3 Months</option>
                       <option value={6}>6 Months</option>
                       <option value={12}>12 Months</option>
                     </select>

                     {advanceMonths > 0 && ratePlan && (
                       <div className="fade-in-up" style={{background: '#f8fdf9', padding: '15px', borderRadius: '8px', border: '1px solid #c8e6c9', marginTop: '15px'}}>
                          {(() => {
                             let baseMonthly = 0;
                             let maintCost = 0;
                             const pType = userRefInfo.propertyType;
                             
                             if (pType === 'houses') {
                                 const size = String(profileData.plotSize || profileData.size_yards || '100');
                                 const sizeRates = ratePlan.components?.[size] || {};
                                 baseMonthly = (Number(sizeRates.s_w) || 0) + (Number(sizeRates.service) || 0);
                                 maintCost = Number(sizeRates.maintenance) || 0;
                             } else if (pType === 'n_home') {
                                 const homeRates = ratePlan.components?.[userRefInfo.plotNo] || {};
                                 baseMonthly = (Number(homeRates.s_w) || 0) + (Number(homeRates.service) || 0);
                                 maintCost = Number(homeRates.maintenance) || 0;
                             } else {
                                 baseMonthly = (Number(ratePlan.components?.s_w) || 0) + (Number(ratePlan.components?.service) || 0);
                                 maintCost = Number(ratePlan.components?.maintenance) || 0;
                             }

                             const getNextMonthStr = (YYYY_MM) => {
                                let [year, month] = YYYY_MM.split('-').map(Number);
                                month++;
                                if (month > 12) { month = 1; year++; }
                                return `${year}-${String(month).padStart(2, '0')}`;
                             };

                             let latestMonth = "2000-01";
                             // Filter out yearly bills to find the actual latest monthly bill
                             const standardBills = bills.filter(b => b.billType !== 'YEARLY' && !b.month.includes('YEARLY'));
                             
                             if (standardBills.length > 0) {
                                 latestMonth = standardBills[0].month; 
                             } else {
                                 const now = new Date();
                                 latestMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                             }

                             let totalBase = 0;
                             const capsules = [];
                             let currentM = latestMonth;
                             
                             for (let i = 0; i < advanceMonths; i++) {
                                 currentM = getNextMonthStr(currentM);
                                 totalBase += baseMonthly;
                                 capsules.push({ month: currentM, cost: baseMonthly, isMaint: false });
                             }

                             let discount = 0;
                             const advRule = ratePlan.advanceBenefitRule || {};
                             let appliedPromo = "";

                             if (advRule.enabled && advanceMonths >= (advRule.minAdvanceMonths || 1) && advanceMonths <= (advRule.maxAdvanceMonths || 12)) {
                                 if (advRule.type === 'PERCENT') {
                                     discount = totalBase * (Number(advRule.value) / 100);
                                     appliedPromo = `${advRule.value}% Off`;
                                 } else if (advRule.type === 'FLAT') {
                                     discount = Number(advRule.value); 
                                     appliedPromo = `₹${discount} Off`;
                                 }
                             }
                             const finalAmount = totalBase - discount;

                             return (
                               <div>
                                 <div style={{fontSize: '12px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '8px'}}>Generating Advance For:</div>
                                 <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px'}}>
                                    {capsules.map((cap, idx) => (
                                        <span key={idx} style={{background: cap.isMaint ? '#fff3e0' : '#e8f5e9', border: `1px solid ${cap.isMaint ? '#ffe0b2' : '#c8e6c9'}`, color: cap.isMaint ? '#e65100' : '#2e7d32', padding: '4px 10px', borderRadius: '15px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                           {cap.month} (₹{cap.cost})
                                        </span>
                                    ))}
                                 </div>
                                 <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px', color: '#555', fontSize: '13px'}}><span>Total Base Charges:</span><span>₹{totalBase}</span></div>
                                 {discount > 0 && <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px', color: '#2e7d32', fontWeight: 'bold', fontSize: '13px'}}><span>🎉 Savings ({appliedPromo}):</span><span>- ₹{discount.toFixed(0)}</span></div>}
                                 <div style={{display:'flex', justifyContent:'space-between', marginTop:'10px', paddingTop:'10px', borderTop:'1px dashed #a5d6a7', fontWeight: 'bold', fontSize: '16px', color: '#1b5e20'}}><span>Total Payable:</span><span>₹{finalAmount.toFixed(0)}</span></div>
                               </div>
                             );
                          })()}
                       </div>
                     )}
                 </div>
                 <button 
                    onClick={() => prepareCheckout(false)} 
                    disabled={advanceMonths === 0 || isMonthlyDefaulter || isProcessingCheckout} 
                    className="btn-primary-full" 
                    style={{background: advanceMonths === 0 ? '#ccc' : '#3498db'}}
                  >
                    {isProcessingCheckout ? "Preparing..." : "Pay Advance"}
                  </button>
               </div>
             </div> {/* <--- THIS CLOSES THE FIRST GRID ---> */}

             {/* --- NEW YEARLY MAINTENANCE SECTION (Only for vacant/unconstructed plots) --- */}
             {(!profileData.isFilled || yearlyUnpaidBills.length > 0) && (
               <>
                 <h2 style={{marginTop: '30px', marginBottom: '20px', color: 'var(--dark-text)'}}>🚧 Yearly Maintenance (Vacant Plot)</h2>
                 <div className="info-grid">
                   
                   {/* YEARLY ARREARS */}
                   <div className="info-card" style={{borderTop: '5px solid #c0392b', display: 'flex', flexDirection: 'column'}}>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '10px', marginTop: '5px' }}>
                        <div className="icon-box-warning">⚠️</div>
                        <h3 style={{ margin: 0 }}>Pending Yearly Dues</h3>
                     </div>
                     <p style={{fontSize: '13px', color: '#666', margin: '0 0 15px 0'}}>Select the pending yearly maintenance bills to clear.</p>
                     
                     <div className="bill-scroll-area-yearly">
                       {yearlyUnpaidBills.length === 0 ? (
                         <p style={{color: '#2e7d32', fontWeight: 'bold', textAlign: 'center', marginTop: '30px'}}>🎉 You have no pending yearly arrears!</p>
                       ) : (
                         <div className="bill-list-container">
                           {yearlyUnpaidBills.map(bill => {
                             const penaltyAmt = bill.penalty?.amount || 0;
                             const totalAmt = bill.totalPayable + penaltyAmt;
                             const isSelected = selectedYearlyBillIds.includes(bill.id);
                             return (
                               <div key={bill.id} className={`bill-row${isSelected ? ' bill-row-selected' : ''}`}>
                                 <label className="bill-row-label">
                                   <input type="checkbox" className="bill-checkbox" checked={isSelected} onChange={() => handleYearlyToggle(bill.id)} />
                                   <div>
                                     <strong className="bill-row-month" style={{color: '#c0392b'}}>Yearly Maintenance ({bill.year})</strong>
                                     <div className="bill-row-due">Due: {new Date(bill.dueDate?.seconds * 1000).toLocaleDateString()}</div>
                                     {bill.breakup && (
                                       <div className="bill-breakup">
                                         <span style={{color: '#c0392b', fontWeight: 'bold'}}>🏗️ Base: ₹{bill.breakup.maintenance}</span>
                                       </div>
                                     )}
                                   </div>
                                 </label>

                                 <div className="bill-row-totals">
                                   {penaltyAmt > 0 && (
                                     <>
                                       <div className="bill-total-line">
                                         <span>Base</span><span>₹{bill.totalPayable}</span>
                                       </div>
                                       <div className="bill-total-line bill-total-penalty">
                                         <span>Penalty</span><span>+₹{penaltyAmt}</span>
                                       </div>
                                     </>
                                   )}
                                   <div className="bill-total-line bill-total-final">
                                     <span>Total</span><span>₹{totalAmt}</span>
                                   </div>
                                 </div>
                               </div>
                             );
                           })}
                         </div>
                       )}
                     </div>
    
                     <button 
                        onClick={prepareYearlyCheckout} 
                        disabled={selectedYearlyBillIds.length === 0 || isProcessingCheckout} 
                        className="btn-primary-full" 
                        style={{background: selectedYearlyBillIds.length === 0 ? '#ccc' : '#c0392b'}}
                      >
                        {isProcessingCheckout ? "Preparing..." : `Pay Selected Yearly Dues (₹ ${selectedYearlyTotal})`}
                      </button>
                   </div>
                   
                   {/* ADVANCE YEARLY */}
                   {profileData.isFilled === false && (
                     <div className={`info-card ${isYearlyDefaulter ? 'locked-section' : ''}`} style={{borderTop: '5px solid #f39c12', display: 'flex', flexDirection: 'column'}}>
                     
                     {isYearlyDefaulter && (
                       <div className="locked-overlay">
                         <span>🔒 Clear Yearly Arrears First</span>
                         <p style={{fontSize: '12px', color: '#333', marginTop: '10px', fontWeight: 'bold', padding: '0 15px'}}>Advance payments are locked. Please pay your pending yearly bills to unlock this feature.</p>
                       </div>
                     )}
    
                     <h3>🚀 Pay Yearly in Advance</h3>
                     <p style={{fontSize: '13px', color: '#666', margin: '0 0 15px 0'}}>Secure future yearly maintenance bills.</p>
                     
                     <div style={{flex: 1, display: 'flex', flexDirection: 'column', background: '#fffcf5', borderRadius: '8px', padding: '20px', marginBottom: '15px', border: '1px solid #fdebd0'}}>
                        <label style={{fontWeight: 'bold', color: '#e67e22', marginBottom: '10px'}}>Select Advance Years:</label>
                        <select className="input-field" style={{marginBottom: '0', padding: '10px', fontSize: '15px', borderColor: '#fad7a1'}} value={advanceYears} onChange={e => setAdvanceYears(Number(e.target.value))}>
                           <option value={0}>-- Choose Years --</option>
                           <option value={1}>1 Year</option>
                           <option value={2}>2 Years</option>
                           <option value={3}>3 Years</option>
                         </select>
                         
                         {advanceYears > 0 && ratePlan && (
                           <div className="fade-in-up" style={{background: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #fdebd0', marginTop: '15px'}}>
                              {(() => {
                                 let maintCost = 0;
                                 const pType = userRefInfo.propertyType;
                                 
                                 if (pType === 'houses') {
                                     const size = String(profileData.plotSize || profileData.size_yards || '100');
                                     maintCost = Number(ratePlan.components?.[size]?.maintenance) || 0;
                                 } else if (pType === 'n_home') {
                                     maintCost = Number(ratePlan.components?.[userRefInfo.plotNo]?.maintenance) || 0;
                                 } else {
                                     maintCost = Number(ratePlan.components?.maintenance) || 0;
                                 }
    
                                 let latestYear = parseInt(new Date().getFullYear()) - 1;
                                 const yearlyBillsOnly = bills.filter(b => b.billType === 'YEARLY');
                                 if (yearlyBillsOnly.length > 0) {
                                     latestYear = Math.max(...yearlyBillsOnly.map(b => parseInt(b.year)));
                                 }
                                 
                                 const totalAdvCost = maintCost * advanceYears;
                                 const capsules = [];
                                 let currentY = latestYear;
                                 
                                 for (let i = 0; i < advanceYears; i++) {
                                     currentY++;
                                     capsules.push({ year: currentY, cost: maintCost });
                                 }
    
                                 return (
                                   <div>
                                     <div style={{fontSize: '12px', fontWeight: 'bold', color: '#e67e22', marginBottom: '8px'}}>Generating Advance For:</div>
                                     <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px'}}>
                                        {capsules.map((cap, idx) => (
                                            <span key={idx} style={{background: '#fff3e0', border: '1px solid #ffe0b2', color: '#e65100', padding: '4px 10px', borderRadius: '15px', fontSize: '11px', fontWeight: 'bold'}}>
                                               Year {cap.year} (₹{cap.cost})
                                            </span>
                                        ))}
                                     </div>
                                     <div style={{display:'flex', justifyContent:'space-between', marginTop:'10px', paddingTop:'10px', borderTop:'1px dashed #fad7a1', fontWeight: 'bold', fontSize: '16px', color: '#d35400'}}><span>Total Payable:</span><span>₹{totalAdvCost}</span></div>
                                   </div>
                                 );
                              })()}
                           </div>
                         )}
                     </div>
                     <button 
                        onClick={prepareYearlyCheckout} 
                        disabled={advanceYears === 0 || isYearlyDefaulter || isProcessingCheckout} 
                        className="btn-primary-full" 
                        style={{background: advanceYears === 0 ? '#ccc' : '#f39c12'}}
                      >
                        {isProcessingCheckout ? "Preparing..." : "Pay Advance Yearly"}
                      </button>
                   </div>
                 )}
                 </div>
               </>
             )}

             {/* --- 4. PAYMENT HISTORY --- */}
             <div className="info-card" style={{borderTop: '5px solid #2e7d32', marginTop: '30px', display: 'flex', flexDirection: 'column'}}>
               {(() => {
                 let latestPaidMonthStr = "None";
                 let latestPaidYearStr = "None";
                 
                 // Only look at standard monthly bills to find the latest cleared month
                 const standardPaidBills = paidBills.filter(b => b.billType !== 'YEARLY' && !b.month.includes('YEARLY'));
                 if (standardPaidBills.length > 0) {
                     latestPaidMonthStr = standardPaidBills.reduce((max, b) => b.month > max ? b.month : max, "2000-01");
                 }

                 // Look at yearly bills to find the latest cleared year
                 const yearlyPaidBills = paidBills.filter(b => b.billType === 'YEARLY');
                 if (yearlyPaidBills.length > 0) {
                     latestPaidYearStr = Math.max(...yearlyPaidBills.map(b => parseInt(b.year))).toString();
                 }

                 // Grouping the Bills and Calculating Breakdown Values dynamically
                 const groupedMap = {};
                 paidBills.forEach(bill => {
                     const refId = bill.paidByPaymentId || 'Manual/Unknown';
                     if (!groupedMap[refId]) {
                         groupedMap[refId] = {
                             refId,
                             date: bill.paidAt?.seconds ? new Date(bill.paidAt.seconds * 1000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A',
                             timestamp: bill.paidAt?.seconds || 0,
                             totalBase: 0,
                             totalPenalty: 0,
                             totalSW: 0,
                             totalService: 0,
                             totalMaint: 0,
                             advBase: 0, // Helps with calculating discount on the fly
                             months: [],
                             isManual: refId.startsWith('MANUAL-')
                         };
                     }
                     const pen = bill.penaltyWaived ? 0 : (bill.penalty?.amount || 0);
                     groupedMap[refId].totalBase += (bill.totalPayable || 0);
                     // Manual-settlement receipts can include an amount that doesn't map to
                     // any specific billed month (earlier pending dues collected alongside
                     // this receipt, or a shortfall written off) - see apply_manual_payments_batch4.py.
                     groupedMap[refId].totalBase += (bill.extraCollected || 0);
                     groupedMap[refId].totalPenalty += pen;

                     // MATH BREAKDOWN
                     if (bill.breakup) {
                         groupedMap[refId].totalSW += (bill.breakup.s_w || 0);
                         groupedMap[refId].totalService += (bill.breakup.service || 0);
                         groupedMap[refId].totalMaint += (bill.breakup.maintenance || 0);
                     } else {
                         // Fallback for older bills
                         groupedMap[refId].totalSW += (bill.totalPayable || 0);
                     }

                     const isAdv = bill.status === 'PAID_IN_ADVANCE';
                     if (isAdv) {
                         groupedMap[refId].advBase += (bill.totalPayable || 0);
                     }

                     groupedMap[refId].months.push({
                         month: bill.billType === 'YEARLY' ? `${bill.year} (Yearly)` : bill.month, 
                         type: isAdv ? 'ADV' : 'ARR',
                         amount: (bill.totalPayable || 0) + pen
                     });
                 });

                 // Calculate Exact Discount & Gateway Fee on the fly
                 Object.values(groupedMap).forEach(group => {
                     let discount = 0;
                     let gateway = 0;
                     
                     // If it's a manual payment, no gateway/discount. Otherwise apply current rates to mimic history.
                     if (!group.isManual && ratePlan) {
                         const advRule = ratePlan.advanceBenefitRule || {};
                         const platRule = ratePlan.platformChargeRule || {};
                         const advCount = group.months.filter(m => m.type === 'ADV').length;
                         
                         // Apply discount to Advance Base
                         if (advRule.enabled && advCount >= (advRule.minAdvanceMonths || 1)) {
                             if (advRule.type === 'PERCENT') discount = Math.round(group.advBase * (Number(advRule.value) / 100));
                             else if (advRule.type === 'FLAT') discount = Number(advRule.value);
                         }
                         
                         let subtotal = group.totalBase + group.totalPenalty - discount;
                         
                         // Calculate Gateway fee against subtotal
                         if (platRule.enabled && platRule.payerSide && subtotal > 0) {
                             if (platRule.percent) gateway += Math.round(subtotal * (Number(platRule.percent) / 100));
                             if (platRule.flat) gateway += Number(platRule.flat);
                         }
                     }
                     
                     group.discount = discount;
                     group.gateway = gateway;
                     group.grandTotal = group.totalBase + group.totalPenalty - discount + gateway;
                 });
                 
                 const groupedPayments = Object.values(groupedMap).sort((a,b) => b.timestamp - a.timestamp);
                 const totalGroupPages = Math.ceil(groupedPayments.length / itemsPerPage) || 1;
                 const currentGroups = groupedPayments.slice((historyPage - 1) * itemsPerPage, historyPage * itemsPerPage);

                 return (
                   <>
                     <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px'}}>
                        <h3 style={{margin: 0}}>🧾 Payment History</h3>
                        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                            <div style={{fontSize: '12px', background: '#f1f5f9', padding: '4px 10px', borderRadius: '15px', color: '#475569', fontWeight: 'bold'}}>
                                Monthly Cleared Upto: <span style={{color: '#10b981'}}>{latestPaidMonthStr}</span>
                            </div>
                            {(!profileData.isFilled || yearlyPaidBills.length > 0) && (
                                <div style={{fontSize: '12px', background: '#fdf2f1', padding: '4px 10px', borderRadius: '15px', color: '#c0392b', fontWeight: 'bold'}}>
                                    Yearly Cleared Upto: <span style={{color: '#e74c3c'}}>{latestPaidYearStr}</span>
                                </div>
                            )}
                        </div>
                     </div>
                     
                     <div style={{flex: 1, minHeight: '150px'}}>
                       {groupedPayments.length === 0 ? <p style={{color:'#888', padding: '20px', textAlign: 'center'}}>No past payments found.</p> : (
                         <div style={{maxHeight:'350px', overflowY:'auto', paddingRight: '5px'}}>
                          {currentGroups.map((group, idx) => {
                              const isExpanded = expandedHistory[group.refId];
                              return (
                                <div key={idx} style={{border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '10px', background: '#fff', overflow: 'hidden'}}>
                                    
                                    {/* Main Row */}
                                    <div 
                                        onClick={() => setExpandedHistory(prev => ({...prev, [group.refId]: !prev[group.refId]}))}
                                        style={{display: 'flex', justifyContent: 'space-between', padding: '15px', cursor: 'pointer', background: isExpanded ? '#f8fafc' : '#fff', transition: 'background 0.2s'}}
                                    >
                                        <div>
                                            <strong style={{color: '#0f172a', fontSize: '15px'}}>{group.date}</strong>
                                            <div style={{fontSize: '11px', color: '#64748b', marginTop: '4px', fontFamily: 'monospace'}}>Ref: {group.refId}</div>
                                        </div>
                                        <div style={{textAlign: 'right'}}>
                                            <strong style={{color: '#10b981', fontSize: '16px'}}>₹ {group.grandTotal.toLocaleString('en-IN')}</strong>
                                            <div style={{fontSize: '11px', color: '#3b82f6', marginTop: '4px', fontWeight: 'bold'}}>
                                                {isExpanded ? 'Hide Details ▲' : 'View Details ▼'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    {isExpanded && (
                                        <div className="fade-in-up" style={{padding: '15px', borderTop: '1px solid #e2e8f0', background: '#f8fafc'}}>
                                            
                                            {/* Mathematics Breakdown */}
                                            <div style={{fontSize: '12px', color: '#475569', marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px dashed #cbd5e1'}}>
                                                
                                                {group.totalSW > 0 && <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>💧 Water & Sewage:</span> <strong>₹{group.totalSW}</strong></div>}
                                                {group.totalService > 0 && <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>🧹 Service Charge:</span> <strong>₹{group.totalService}</strong></div>}
                                                {group.totalMaint > 0 && <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>🚧 Yearly Maintenance:</span> <strong style={{color: '#c0392b'}}>₹{group.totalMaint}</strong></div>}
                                                
                                                {group.totalPenalty > 0 && <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>⚠️ Late Penalty:</span> <strong style={{color: '#ef4444'}}>₹{group.totalPenalty}</strong></div>}
                                                {group.discount > 0 && <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>🎁 Advance Discount:</span> <strong style={{color: '#10b981'}}>-₹{group.discount}</strong></div>}
                                                {group.gateway > 0 && <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>💳 Gateway Convenience Fee:</span> <strong>₹{group.gateway}</strong></div>}
                                                
                                                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0', fontSize: '14px'}}>
                                                    <span><strong>Total Paid:</strong></span> 
                                                    <strong style={{color: '#0f172a'}}>₹{group.grandTotal}</strong>
                                                </div>
                                            </div>

                                            {/* Month Capsules */}
                                            <div style={{fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase'}}>Months Cleared in this payment:</div>
                                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                                                {group.months.map((m, i) => (
                                                    <div key={i} style={{background: m.type === 'ADV' ? '#eff6ff' : '#f1f5f9', border: `1px solid ${m.type === 'ADV' ? '#bfdbfe' : '#e2e8f0'}`, color: m.type === 'ADV' ? '#1d4ed8' : '#334155', padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold'}}>
                                                        {m.month} <span style={{opacity: 0.6, fontWeight: 'normal', marginLeft: '2px'}}>(₹{m.amount})</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                              )
                          })}
                         </div>
                       )}
                     </div>

                     {/* History Pagination */}
                     {groupedPayments.length > itemsPerPage && (
                       <div className="pagination-controls" style={{marginTop: '15px'}}>
                          <button className="btn-page" disabled={historyPage === 1} onClick={() => setHistoryPage(p => p - 1)}>← Prev</button>
                          <span className="page-info">Page {historyPage} of {totalGroupPages}</span>
                          <button className="btn-page" disabled={historyPage === totalGroupPages} onClick={() => setHistoryPage(p => p + 1)}>Next →</button>
                       </div>
                     )}
                   </>
                 );
               })()}
             </div>

           </div>
        )}

        {currentView === 'settings' && (
          <div className="floating-card settings-form fade-in-up">
            <h3 style={{marginTop: 0, color: 'var(--primary)', borderBottom: '2px solid #eee', paddingBottom: '15px', marginBottom: '25px'}}>Update Personal Details</h3>
            <form onSubmit={handleSaveSettings}>
              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Owner Full Name</label>
              <input className="input-field" value={editForm.ownerName} onChange={e => setEditForm({...editForm, ownerName: e.target.value})} required />
              <div className="row-inputs">
                <div style={{flex: 1}}><label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Phone Number</label><input className="input-field" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} required /></div>
                <div style={{flex: 1}}><label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>UID / ID Number</label><input className="input-field" value={editForm.uidNo} onChange={e => setEditForm({...editForm, uidNo: e.target.value})} /></div>
              </div>
              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Residential Address</label>
              <textarea className="input-field" rows="2" value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} required />
              <div className="password-box">
                <h4>🔒 Change Login Password (Optional)</h4>
                <p style={{fontSize: '11px', color: '#888', marginTop: '-10px', marginBottom: '15px'}}>Leave blank if you do not want to change your current password.</p>
                <div className="row-inputs">
                  <div style={{flex: 1}}><input type="password" className="input-field" placeholder="Enter New Password" value={editForm.newPassword} onChange={e => setEditForm({...editForm, newPassword: e.target.value})} /></div>
                  <div style={{flex: 1}}><input type="password" className="input-field" placeholder="Confirm New Password" value={editForm.confirmPassword} onChange={e => setEditForm({...editForm, confirmPassword: e.target.value})} /></div>
                </div>
              </div>
              <div style={{display:'flex', gap:'15px'}}>
                <button type="button" onClick={() => setCurrentView('dashboard')} className="btn-primary-full" style={{background: '#e0e0e0', color: '#333'}}>Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary-full">{saving ? "Saving Changes..." : "💾 Save Changes"}</button>
              </div>
            </form>
          </div>
        )}

      </div>

      {/* --- DEVELOPER AD LINK --- */}
      <div style={{ textAlign: 'center', padding: '30px 20px', marginTop: 'auto' }}>
        <button 
          onClick={() => navigate('/developer-ad')} 
          style={{ background: 'none', border: 'none', color: '#7f8c8d', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
        >
          Boost your Business, make your own website. Contact Developer →
        </button>
      </div>

      {/* --- DEFAULT PASSWORD PROMPT MODAL --- */}
      {showPasswordPrompt && (
        <div className="modal-overlay" style={{zIndex: 1000}}>
          <div className="modal-box" style={{maxWidth: '450px', background: '#fff'}}>
            <div className="modal-header" style={{borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
              <h3 style={{color: '#d35400', margin: 0}}>⚠️ Security Alert</h3>
              <button onClick={() => setShowPasswordPrompt(false)} className="btn-close" style={{color: '#999'}}>✖</button>
            </div>
            <div className="modal-body" style={{padding: '20px'}}>
              <p style={{fontSize: '14px', color: '#555', marginBottom: '20px', lineHeight: '1.5'}}>
                You are currently using the default password (your plot number). For the security of your ledger and payments, please update your password now.
              </p>
              <form onSubmit={handlePromptPasswordSave}>
                <input 
                    type="password" 
                    className="input-field" 
                    placeholder="New Password (min 6 chars)" 
                    value={promptPasswordForm.newPassword} 
                    onChange={e => setPromptPasswordForm({...promptPasswordForm, newPassword: e.target.value})} 
                    required 
                    style={{marginBottom: '10px'}}
                />
                <input 
                    type="password" 
                    className="input-field" 
                    placeholder="Confirm New Password" 
                    value={promptPasswordForm.confirmPassword} 
                    onChange={e => setPromptPasswordForm({...promptPasswordForm, confirmPassword: e.target.value})} 
                    required 
                    style={{marginBottom: '20px'}}
                />
                <button type="submit" disabled={promptSaving} className="btn-primary-full" style={{background: promptSaving ? '#ccc' : '#27ae60'}}>
                    {promptSaving ? "Saving..." : "💾 Update Password"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- PAYMENT RESTRICTION MODAL --- */}
      {paymentRestrictionMsg && (
        <div className="modal-overlay" style={{zIndex: 1000}}>
          <div className="modal-box" style={{maxWidth: '450px', background: '#fffcfc', borderTop: '5px solid #c0392b'}}>
            <div className="modal-header" style={{borderBottom: 'none'}}>
              <h3 style={{color: '#c0392b', margin: 0}}>⚠️ Payment Restricted</h3>
              <button onClick={() => setPaymentRestrictionMsg(null)} className="btn-close" style={{color: '#c0392b'}}>✖</button>
            </div>
            <div className="modal-body" style={{padding: '10px 20px 20px 20px', textAlign: 'center'}}>
              <div style={{fontSize: '40px', marginBottom: '15px'}}>🛑</div>
              <p style={{fontSize: '15px', color: '#333', lineHeight: '1.5', fontWeight: '500'}}>
                {paymentRestrictionMsg}
              </p>
            </div>
            <div className="modal-footer" style={{display: 'flex', justifyContent: 'center', background: '#fef5f5', borderRadius: '0 0 12px 12px'}}>
              <button 
                onClick={() => setPaymentRestrictionMsg(null)} 
                className="btn-primary-full" 
                style={{background: '#c0392b', color: '#fff', width: 'auto', padding: '10px 30px'}}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- RETURN TO APP MODAL (shown only when payment started from the mobile app) --- */}
      {showReturnToAppPrompt && (
        <div className="modal-overlay" style={{zIndex: 1000}}>
          <div className="modal-box" style={{maxWidth: '420px', background: '#fdfdfd', borderTop: '5px solid #27ae60'}}>
            <div className="modal-header" style={{borderBottom: 'none'}}>
              <h3 style={{color: '#27ae60', margin: 0}}>✅ Payment Successful</h3>
            </div>
            <div className="modal-body" style={{padding: '10px 20px 20px 20px', textAlign: 'center'}}>
              <div style={{fontSize: '40px', marginBottom: '15px'}}>🎉</div>
              <p style={{fontSize: '15px', color: '#333', lineHeight: '1.5'}}>
                Your ledger has been updated. Would you like to go back to the Housefed Colony app?
              </p>
            </div>
            <div className="modal-footer" style={{display: 'flex', justifyContent: 'center', gap: '12px', background: '#f5fef8', borderRadius: '0 0 12px 12px'}}>
              <button
                onClick={() => setShowReturnToAppPrompt(false)}
                className="btn-primary-full"
                style={{background: '#eee', color: '#333', width: 'auto', padding: '10px 24px'}}
              >
                Stay Here
              </button>
              <button
                onClick={() => {
                  sessionStorage.removeItem('housefed_from_app');
                  setShowReturnToAppPrompt(false);
                  window.location.href = 'housefedapp://payment-complete';
                }}
                className="btn-primary-full"
                style={{background: '#27ae60', color: '#fff', width: 'auto', padding: '10px 24px'}}
              >
                Return to App
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- PRE-CHECKOUT RECEIPT MODAL --- */}
      {checkoutData && (
        <div className="modal-overlay" style={{zIndex: 1000}}>
          <div className="modal-box" style={{maxWidth: '450px', background: '#fdfdfd'}}>
            
            {/* Header - Fixed Text Color Issue */}
            <div className="modal-header" style={{borderBottom: 'none'}}>
              <h3 style={{color: '#ffffff', margin: 0}}>🧾 Payment Summary</h3>
              <button onClick={() => setCheckoutData(null)} className="btn-close" style={{color: '#fff'}}>✖</button>
            </div>
            
            <div className="modal-body" style={{padding: '0 20px 20px 20px'}}>
              <div style={{background: 'white', border: '1px solid #eee', borderRadius: '8px', padding: '15px', maxHeight: '50vh', overflowY: 'auto'}}>
                
                {/* 1. INDIVIDUAL ARREARS BREAKDOWN */}
                {checkoutData.breakdown.arrearsDetails && checkoutData.breakdown.arrearsDetails.length > 0 && (
                  <div style={{marginBottom: '15px'}}>
                    <h4 style={{margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px'}}>Pending Dues</h4>
                    
                    {checkoutData.breakdown.arrearsDetails.map((item, idx) => (
                      <div key={idx} style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px'}}>
                        <span>{item.month} (Base)</span>
                        <span>₹{item.base}</span>
                      </div>
                    ))}
                    
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#333', fontWeight: 'bold', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #eee'}}>
                       <span>Total Base Arrears</span>
                       <span>₹{checkoutData.breakdown.baseArrears}</span>
                    </div>

                    {/* Penalty Display */}
                    {checkoutData.breakdown.penaltiesOriginal > 0 && (
                      <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '8px'}}>
                        <span style={{color: '#666'}}>Late Penalties</span>
                        {checkoutData.waivePenalty && checkoutData.breakdown.penaltiesWaived > 0 ? (
                          <div style={{textAlign: 'right'}}>
                            <div>
                                <span style={{textDecoration: 'line-through', color: '#888', marginRight: '5px'}}>₹{checkoutData.breakdown.penaltiesOriginal}</span>
                                <span style={{color: '#27ae60', fontWeight: 'bold'}}>-₹{checkoutData.breakdown.penaltiesWaived} WAIVED</span>
                            </div>
                            {/* NEW: Explicitly show the remaining penalty if the waiver was capped at 12 months */}
                            {(checkoutData.breakdown.penaltiesOriginal - checkoutData.breakdown.penaltiesWaived) > 0 && (
                                <div style={{color: '#c0392b', fontWeight: 'bold', marginTop: '4px'}}>
                                    ₹{checkoutData.breakdown.penaltiesOriginal - checkoutData.breakdown.penaltiesWaived} (Remaining)
                                </div>
                            )}
                          </div>
                        ) : (
                          <strong style={{color: '#c0392b'}}>₹{checkoutData.breakdown.penaltiesOriginal}</strong>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* 2. INDIVIDUAL ADVANCE MONTHS BREAKDOWN */}
                {checkoutData.breakdown.advanceDetails && checkoutData.breakdown.advanceDetails.length > 0 && (
                  <div style={{marginBottom: '15px', paddingTop: '10px', borderTop: '2px solid #f0f0f0'}}>
                    <h4 style={{margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '5px'}}>Advance Payments</h4>
                    
                    {checkoutData.breakdown.advanceDetails.map((item, idx) => (
                      <div key={idx} style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px'}}>
                        <span>Advance: {item.month}</span>
                        <span>₹{item.amount}</span>
                      </div>
                    ))}

                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#333', fontWeight: 'bold', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #eee'}}>
                       <span>Total Advance Base</span>
                       <span>₹{checkoutData.breakdown.advanceBaseAmount}</span>
                    </div>

                    {/* Advance Discount */}
                    {checkoutData.breakdown.advanceDiscount > 0 && (
                      <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '8px'}}>
                        <span style={{color: '#27ae60'}}>Advance Discount</span>
                        <strong style={{color: '#27ae60'}}>- ₹{checkoutData.breakdown.advanceDiscount}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* 2B. INDIVIDUAL ADVANCE YEARS BREAKDOWN */}
                {checkoutData.breakdown.advanceYearlyDetails && checkoutData.breakdown.advanceYearlyDetails.length > 0 && (
                  <div style={{marginBottom: '15px', paddingTop: '10px', borderTop: '2px solid #f0f0f0'}}>
                    <h4 style={{margin: '0 0 8px 0', color: '#c0392b', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '5px'}}>Advance Yearly Maintenance</h4>
                    
                    {checkoutData.breakdown.advanceYearlyDetails.map((item, idx) => (
                      <div key={idx} style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px'}}>
                        <span>Advance: Year {item.year}</span>
                        <span>₹{item.amount}</span>
                      </div>
                    ))}

                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#333', fontWeight: 'bold', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #eee'}}>
                       <span>Total Advance Yearly</span>
                       <span>₹{checkoutData.breakdown.advanceYearlyAmount}</span>
                    </div>
                  </div>
                )}

                {/* 3. PLATFORM FEE */}
                {checkoutData.breakdown.platformFee > 0 && (
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px', paddingTop: '10px', borderTop: '2px dashed #f0f0f0'}}>
                    <span style={{color: '#888', fontSize: '12px'}}>Gateway Convenience Fee</span>
                    <span style={{color: '#888', fontSize: '12px'}}>₹{checkoutData.breakdown.platformFee}</span>
                  </div>
                )}

                {/* 4. FINAL PAYABLE */}
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #34495e', fontSize: '18px'}}>
                  <span style={{fontWeight: 'bold', color: '#2c3e50'}}>Total to Pay:</span>
                  <strong style={{color: '#3498db'}}>₹{checkoutData.breakdown.finalPayableRupees}</strong>
                </div>

              </div>
            </div>

            <div className="modal-footer" style={{display: 'flex', gap: '15px', background: '#f8f9fa', borderRadius: '0 0 12px 12px'}}>
              <button 
                onClick={() => setCheckoutData(null)} 
                disabled={isVerifying} 
                className="btn-primary-full" 
                style={{background: '#e0e0e0', color: '#333', opacity: isVerifying ? 0.5 : 1, cursor: isVerifying ? 'not-allowed' : 'pointer'}}
              >
                Cancel
              </button>
              <button 
                onClick={launchRazorpay} 
                disabled={isVerifying} 
                className="btn-primary-full" 
                style={{background: isVerifying ? '#7f8c8d' : '#2c3e50', cursor: isVerifying ? 'not-allowed' : 'pointer'}}
              >
                {isVerifying ? "⏳ Verifying Payment..." : "💳 Proceed to Pay"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResidentDashboard;